/**
 * web/src/projection/guards.ts —— 投影 DTO 运行时守卫
 *
 * 形状唯一源＝./dto.schema.json（main 面，只读消费）。本件把 schema 的
 * required／enum／const／additionalProperties 规则落为运行时校验：
 *   - isRecord / isSlotPointer / isViewAssembly / isRenderDesc / isApprovalCard
 *   - isEventFrame / isSnapshotFrame / isCommandAckFrame：**转发 `transport/frame`
 *     的唯一解析器**（★L-AU-FE-A3「并」补全——原为同构平行判别，实测与活链路
 *     互有松紧：「更松」收 3 键越字段帧、「更严」拒后端实况扩展载荷 `{assembly,…}`；
 *     现三型判别一并收口唯一形状源，本件不再复制形状规则）
 *   - parseChannelFrame／isChannelFrame：**转发 `transport/frame` 的唯一解析器**
 *     （★L-AU-FE-P01 审计整改：原为同构平行面——本文件此二符号与
 *     `transport/frame.toFrame` 各自实现一套判别，「互有松紧、无一支配」。
 *     现并入唯一链路：全树帧形状规则只留 transport/frame 一处；本件对形
 *     `Frame`（ChannelFrame）做判别转发，抛错面（frameInvalid）同源。
 *     调用方记日志、不抛错不断流的口径由本包装维持。）
 *   - pickDeclaredFields：字段可见性按声明（判据 i6_view_fields_visibility_declared
 *     的消费面落点）：RenderDesc.fields 之外的键一律不透出。
 * 纪律：本目录不做任何取数（不连 DB/FS、不自建 SQL），只消费传入的投影数据。
 */

import {
  ApprovalCard,
  ChannelFrame,
  CommandAckFrame,
  CommandAckStatus,
  EventFrame,
  FIELD_KEYS,
  FieldKey,
  ProjectionDto,
  RenderDesc,
  SlotPointer,
  SnapshotFrame,
  ViewAssembly,
  ViewFact,
} from "./types";
import { toFrame } from "../transport/frame";
import { TransportError } from "../transport/errors";

// ── 基础 ─────────────────────────────────────────────────────────────
export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

// ── SlotPointer：required [kind, pointer]；kind enum；禁扩字段 ───────
export function isSlotPointer(value: unknown): value is SlotPointer {
  if (!isRecord(value)) return false;
  const keys = Object.keys(value);
  if (keys.length !== 2) return false; // additionalProperties: false
  if (!keys.includes("kind") || !keys.includes("pointer")) return false;
  return (
    (value.kind === "history" || value.kind === "rows") && // kind enum
    typeof value.pointer === "string"
  );
}

// ── ViewAssembly：required [fact, slots]；禁扩字段 ───────────────────
export function isViewFact(value: unknown): value is ViewFact {
  return isRecord(value); // additionalProperties: true，只要求是结构化对象
}

export function isViewAssembly(value: unknown): value is ViewAssembly {
  if (!isRecord(value)) return false;
  const keys = Object.keys(value);
  if (keys.length !== 2) return false; // additionalProperties: false
  if (!keys.includes("fact") || !keys.includes("slots")) return false;
  if (!isViewFact(value.fact)) return false;
  if (!Array.isArray(value.slots)) return false;
  return value.slots.every(isSlotPointer);
}

// ── RenderDesc：required [title_key, fields, pointers]；禁扩字段；
//    fields ⊆ 九键封闭枚举；pointers 项为 SlotPointer ─────────────────
export function isFieldKey(value: unknown): value is FieldKey {
  return (
    typeof value === "string" &&
    (FIELD_KEYS as readonly string[]).includes(value)
  );
}

export function isRenderDesc(value: unknown): value is RenderDesc {
  if (!isRecord(value)) return false;
  const keys = Object.keys(value);
  if (keys.length !== 3) return false; // additionalProperties: false
  if (
    !keys.includes("title_key") ||
    !keys.includes("fields") ||
    !keys.includes("pointers")
  )
    return false;
  if (typeof value.title_key !== "string") return false;
  if (!Array.isArray(value.fields)) return false;
  if (!value.fields.every(isFieldKey)) return false; // 九键枚举
  if (!Array.isArray(value.pointers)) return false;
  return value.pointers.every(isSlotPointer);
}

// ── ApprovalCard：required [surface === "approval"]；可扩字段 ────────
export function isApprovalCard(value: unknown): value is ApprovalCard {
  return isRecord(value) && value.surface === "approval";
}

// ── 投影 DTO 三型判别 ────────────────────────────────────────────────
export function isProjectionDto(value: unknown): value is ProjectionDto {
  return (
    isViewAssembly(value) || isRenderDesc(value) || isApprovalCard(value)
  );
}

// ── 帧三型判别（★L-AU-FE-A3「并」补全：唯一形状源＝transport/frame.toFrame）────
// 口径：判定一律走唯一解析器 `parseChannelFrame`（其内＝`toFrame`）；本件**不再复制**
// required／键闭集／词域/DTO 规则（原平行实现实测与活链路互有松紧：3 键越字段帧误收、
// 后端实况扩展载荷 `{assembly,…}` 误拒）。三型判别＝解析成功后的帧型归属。
export function isEventFrame(value: unknown): value is EventFrame {
  const parsed = parseChannelFrame(value);
  return parsed.ok && parsed.frame !== undefined && "name" in parsed.frame;
}

export function isSnapshotFrame(value: unknown): value is SnapshotFrame {
  const parsed = parseChannelFrame(value);
  return parsed.ok && parsed.frame !== undefined && "views" in parsed.frame;
}

export function isCommandAckFrame(value: unknown): value is CommandAckFrame {
  const parsed = parseChannelFrame(value);
  return parsed.ok && parsed.frame !== undefined && "status" in parsed.frame;
}

// ── command_ack（第三帧型，CONTRACT §3 v1.5；v1.7 补 client_id）────────
// required [id, kind, status]；status ∈ {accepted, rejected, error}；
// 键闭集 ＝ 后端 REPLY_KEYS（id/kind/status/reply/event/error/client_id）；值型校验逐键。
// 🔴 本帧**不入**事件词域（「命令是请求不是事件」）——`kind` 不做词域查表。
// ★L-AU-FE-A3：键闭集/required/值型的**判别**已随三型判别一并收口 `transport/frame`
// （`checkCommandAck`）；下列常量保留＝对外发布的 status 词域（`projection/index` 再导出）。
export const COMMAND_ACK_STATUSES: readonly CommandAckStatus[] = [
  "accepted", "rejected", "error",
];

// ── 帧入口（唯一解析器在 transport/frame；本件只转发，不第二实现） ────
export interface ParsedFrame {
  ok: boolean;
  frame?: ChannelFrame;
  reason?: string;
}

/**
 * 通道帧解析入口：转发 `transport/frame.toFrame`（唯一解析器，★L-AU-FE-P01）。
 * 形状不对 ⇒ 返回 {ok:false, reason}（调用方记日志、计数、可触发重连），
 * **不抛错、不猜形状、不修数据**——本件消化唯一解析器的抛错面。
 */
export function parseChannelFrame(value: unknown): ParsedFrame {
  try {
    return { ok: true, frame: toFrame(value) };
  } catch (err) {
    return {
      ok: false,
      reason: err instanceof TransportError ? err.code : "frame_shape_mismatch",
    };
  }
}

/** 三型判别：转发唯一解析器（形状不合约 ⇒ false，不抛）。 */
export function isChannelFrame(value: unknown): value is ChannelFrame {
  return parseChannelFrame(value).ok;
}

// ── 字段可见性按声明（消费面落点） ───────────────────────────────────
/**
 * 只透出 RenderDesc.fields **声明过**的字段：从 fact 按声明序取值，
 * 未声明键一律不出现（判据 i6_view_fields_visibility_declared）。
 */
export function pickDeclaredFields(
  fact: ViewFact,
  desc: RenderDesc,
): Array<{ key: FieldKey; value: unknown }> {
  return desc.fields
    .filter((key) => key in fact)
    .map((key) => ({ key, value: fact[key] }));
}
